import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Heart, ArrowLeft, ShoppingBag, Leaf, Star, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Navbar from "../components/Navbar";

const WishlistPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(response.data.products);
    } catch (error) {
      console.error("Failed to fetch wishlist");
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      await axios.post(`${API}/wishlist/${productId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(prev => prev.filter(p => p.id !== productId));
      toast.success("Removed from favourites");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#5C5D5C] hover:text-[#1A1B1A] mb-8">
          <ArrowLeft size={20} /> Back
        </button>

        <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#1A1B1A] mb-2">
          My Favourites
        </h1>
        <p className="text-[#5C5D5C] font-light mb-8">{wishlist.length} items saved</p>

        {wishlist.length === 0 ? (
          <div className="text-center py-20 bg-white border border-[#EAE6DF]">
            <Heart size={48} className="mx-auto text-[#8E8F8E] mb-4" />
            <p className="text-[#5C5D5C] text-lg mb-4">Your favourites list is empty</p>
            <Button
              onClick={() => navigate("/products")}
              className="bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none"
              data-testid="browse-products-btn"
            >
              Browse Products
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="wishlist-grid">
            {wishlist.map((product, index) => (
              <div
                key={product.id}
                className="product-card bg-white overflow-hidden relative"
                data-testid={`wishlist-item-${index}`}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); removeFromWishlist(product.id); }}
                  className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors shadow-sm"
                  data-testid={`remove-wishlist-${index}`}
                >
                  <Heart size={18} className="fill-[#F06570] text-[#F06570]" />
                </button>
                <div
                  className="cursor-pointer"
                  onClick={() => navigate(`/products/${product.id}`)}
                >
                  <div className="aspect-square overflow-hidden bg-[#EFECE5]">
                    <img
                      src={product.images?.[0] || "https://via.placeholder.com/400"}
                      alt={product.name}
                      className="w-full h-full object-cover product-image"
                    />
                  </div>
                  <div className="p-4">
                    {product.eco_sustainable && (
                      <span className="inline-flex items-center gap-1 text-xs text-[#3E6656] bg-[#3E6656]/10 px-2 py-1 rounded mb-2">
                        <Leaf size={12} /> Eco
                      </span>
                    )}
                    <h3 className="font-medium text-[#1A1B1A] mt-1 line-clamp-1">{product.name}</h3>
                    <p className="text-sm text-[#5C5D5C] mt-1 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-lg font-semibold text-[#1A1B1A]">£{product.price}</span>
                      {product.rating > 0 && (
                        <span className="flex items-center gap-1 text-sm text-[#5C5D5C]">
                          <Star size={14} className="fill-[#D4A373] text-[#D4A373]" />
                          {product.rating}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
